import { Text, View, Page, Document, StyleSheet, Image } from '@react-pdf/renderer';
import { images } from '~/assets';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { DATE_FORMAT } from '~/utils/constants/common';
import { IActivityReport } from '~/utils/interface/exam';
import { IActivityReportTypeEnum } from '~/utils/enum/common';

export const DocumentCourseActivityReportPDF = ({ data }: { data: any }) => {
  const { student, courses } = data;
  const { t } = useTranslation(['common/course-details-report']);

  const styles = StyleSheet.create({
    page: {
      fontSize: 11,
      paddingTop: 20,
      paddingLeft: 40,
      paddingRight: 40,
      paddingBottom: 40,
      lineHeight: 1.5,
      flexDirection: 'column',
    },
    container: { flexDirection: 'column', flex: 1 },
    logo: { width: 200 },
    line: { borderTopWidth: 5, borderTopColor: '#2b7abc', marginVertical: 24 },
    title: {
      fontSize: 20,
      fontWeight: 'bold',
      color: 'black',
      textAlign: 'center',
      marginBottom: 20,
    },
    table: {
      width: '100%',
      marginBottom: 16,
    },
    tableRow: { flexDirection: 'row' },
    tableCellLabel: {
      width: 180,
      padding: 5,
      color: 'black',
      fontWeight: 'bold',
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: 'black',
      borderStyle: 'solid',
      textAlign: 'left',
      backgroundColor: '#fff',
    },
    tableCell: {
      flex: 1,
      padding: 5,
      color: 'black',
      fontWeight: 'normal',
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: 'black',
      borderStyle: 'solid',
      textAlign: 'left',
      backgroundColor: '#fff',
    },
    tableHeader: {
      flex: 1,
      padding: 5,
      fontWeight: 'bold',
      color: 'black',
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: 'black',
      borderStyle: 'solid',
      textAlign: 'left',
      backgroundColor: '#fff',
    },
    date: { textAlign: 'right', marginTop: 24, color: 'black' },
  });

  const mainCourse = courses?.[0] ?? {};
  const lessonActivities: IActivityReport[] = mainCourse.lessonActivities ?? [];
  const educationActivities: IActivityReport[] = mainCourse.educationActivities ?? [];

  const formatDuration = (ms: number) => {
    const totalMinutes = Math.ceil(ms / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    let result = [];
    if (hours) {
      result.push(hours > 1 ? t('hours_plural', { count: hours }) : t('hours', { count: hours }));
    }
    if (minutes || result.length === 0) {
      result.push(minutes > 1 ? t('minutes_plural', { count: minutes }) : t('minutes', { count: minutes }));
    }
    return result.join(', ');
  };

  return (
    <Document>
      <Page size='A4' style={styles.page} wrap>
        <View style={styles.container}>
          <Image style={styles.logo} source={images.commonLogoFullPositive} />
          <View style={styles.line} />
          <Text style={styles.title}>{t('student_information')}</Text>

          {/* STUDENT INFORMATION TABLE */}
          <View style={styles.table}>
            <View style={styles.tableRow} wrap={false}>
              <View style={[styles.tableCellLabel, { borderLeftWidth: 1, borderTopWidth: 1 }]}>
                <Text>{t('student_name')}</Text>
              </View>
              <View style={[styles.tableCell, { borderTopWidth: 1 }]}>
                <Text>{student?.name}</Text>
              </View>
            </View>
            <View style={styles.tableRow} wrap={false}>
              <View style={[styles.tableCellLabel, { borderLeftWidth: 1 }]}>
                <Text>{t('student_id')}</Text>
              </View>
              <View style={styles.tableCell}>
                <Text>{student?.id}</Text>
              </View>
            </View>
            <View style={styles.tableRow} wrap={false}>
              <View style={[styles.tableCellLabel, { borderLeftWidth: 1 }]}>
                <Text>{t('student_identification_number')}</Text>
              </View>
              <View style={styles.tableCell}>
                <Text>{student?.identificationNumber}</Text>
              </View>
            </View>
          </View>

          {/* LESSON TABLE */}
          <Text style={styles.title}>{t('lesson_title')}</Text>
          <View style={styles.table}>
            <View style={styles.tableRow} wrap={false}>
              <View style={[styles.tableHeader, { borderLeftWidth: 1, borderTopWidth: 1 }]}>
                <Text>{t('lesson_name')}</Text>
              </View>
              <View style={[styles.tableHeader, { borderTopWidth: 1 }]}>
                <Text>{t('date_time')}</Text>
              </View>
            </View>
            {lessonActivities.map((item, idx: number) => (
              <View style={styles.tableRow} key={idx} wrap={false}>
                <View style={[styles.tableCell, { borderLeftWidth: 1 }]}>
                  <Text>{item.name}</Text>
                </View>
                <View style={styles.tableCell}>
                  <Text>{item.dateTime ? moment(item.dateTime).format(DATE_FORMAT.PDF_DATE) : ''}</Text>
                </View>
              </View>
            ))}
          </View>

          {/* ACTIVITY TABLE */}
          <Text style={styles.title}>{t('activity_title')}</Text>
          <View style={styles.table}>
            <View style={styles.tableRow} wrap={false}>
              <View style={[styles.tableHeader, { borderLeftWidth: 1, borderTopWidth: 1 }]}>
                <Text>{t('activity_name')}</Text>
              </View>
              <View style={[styles.tableHeader, { borderTopWidth: 1 }]}>
                <Text>{t('date_time')}</Text>
              </View>
            </View>
            {educationActivities.map((item, idx: number) => {
              let dateLabel = item.dateTime ? moment(item.dateTime).format(DATE_FORMAT.PDF_DATE) : '';
              if (item.dateTime && item.type === IActivityReportTypeEnum.DISCUSSION && item.totalMinute) {
                dateLabel = `${formatDuration(item.totalMinute * 60000)} - ${moment(item.dateTime).format(DATE_FORMAT.DATE)}`;
              }

              return (
                <View style={styles.tableRow} key={idx} wrap={false}>
                  <View style={[styles.tableCell, { borderLeftWidth: 1 }]}>
                    <Text>{item.name}</Text>
                  </View>
                  <View style={styles.tableCell}>
                    <Text>{dateLabel}</Text>
                  </View>
                </View>
              );
            })}
          </View>

          <Text style={styles.date}>
            {t('date_label')} {moment().format(DATE_FORMAT.DATE)}
          </Text>
        </View>
      </Page>
    </Document>
  );
};
